import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape, MessageDescriptor } from 'react-intl';
import { AccountAdminSettingEntity } from '../common/account-admin-setting.entity';
import { ORDER_BOOKING_DATA_UPDATED_TEXT } from '../account-admin.text';
import { BaseAccountAdminSettingViewModel } from '../common/base-account-admin-setting-form.viewmodel';
import { IEventBlueprintDTO } from '../../../api/blueprints/event.blueprint.types';
import { IBlueprintsDAO } from '../../../api/blueprints/blueprints.dao';
import { handleApiException } from '../../global/modal/alerts-host/alert-templates';
import { IReactIntlProvider } from '../../../infrastructure/react-intl-provider';
import { OrderBookingSubsets } from '../../../api/account-admin';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../infrastructure/sub-windows';
import { ADD_EVENT_BLUEPRINT_LABEL_TEXT, CREATE_EVENT_BLUEPRINT_TITLE_TEXT, ENTER_MAXIMUM_TEN_EMAILS } from '../../../constants/order-booking.constants';
import { OrderBookingEventBlueprintForm } from './order-booking-event-blueprints/order-booking-event-blueprint-form';
import { IOrderBookingBlueprintDTO } from '../../../api/blueprints/blueprint.types';
import { emailListValidator } from '../../../modules/validators';
import { ORDERBOOKING_EMAIL_MAX_EMAILS_AMOUNT } from '../../../constants/common.constants';
import { EventBlueprintForm } from '../../_blueprints/event-blueprints/event-blueprint-form/event-blueprint-form';
import { TEventTypeLabel } from '../../../api/events.dao';

export interface ExtendedEventBlueprintDTO extends IEventBlueprintDTO {
    disabled?: boolean;
}

class OrderBookingFormVM extends BaseAccountAdminSettingViewModel {
    @mobx.observable
    public isLoading: boolean = false;
    @mobx.observable
    public eventBlueprints: IEventBlueprintDTO[] = [];
    @mobx.observable
    public selectedBlueprints: IEventBlueprintDTO[] = [];
    @mobx.observable
    public removedBlueprints: IEventBlueprintDTO[] = [];
    @mobx.observable
    public isUpdating: boolean = false;
    @mobx.observable
    private newEventBlueprintSubWindow: ISubWindowFacade | null = null;
    @mobx.observable
    private createEventBlueprintSubWindow: ISubWindowFacade | null = null;
    @mobx.observable
    public internalNotificationEmails: string = '';
    @mobx.observable
    public isFormChecked: boolean = false;
    constructor(
        protected readonly entity: AccountAdminSettingEntity,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject protected readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) {
        super(entity);
        this.initialize();
        const additionalData = (this.entity?.additionalData as string[]) ?? [];
        this.setOrderBookingEmails(additionalData?.join(','));
    }

    public get intl(): IntlShape { return this.intlProvider.getIntl(); }
    @mobx.action
    public async initialize(): Promise<void> {
        try {
            mobx.runInAction(() => {
                this.isLoading = true;
            });
            const params = { accountId: this.entity.accountId, };
            const blueprintsResponse = await this.blueprintsDAO.getEventBlueprintList(params);
            mobx.runInAction(() => {
                this.eventBlueprints = blueprintsResponse;
                this.selectedBlueprints = this.eventBlueprints.filter(blueprint =>
                    Array.isArray(blueprint.orderBookingSubset) && blueprint.orderBookingSubset.includes(OrderBookingSubsets.DEFAULT)
                );
            });
        } catch (error) {
            handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    public sortedBlueprints = (): IEventBlueprintDTO[] => {
        return this.selectedBlueprints.slice().sort((a, b) => a.name.localeCompare(b.name));
    };

    @mobx.action
    public removeEventBlueprint = (blueprintId: string): void => {
        const blueprintIndex = this.selectedBlueprints.findIndex(blueprint => blueprint.blueprintId === blueprintId);
        if (blueprintIndex !== -1) {
            const [removedBlueprint] = this.selectedBlueprints.splice(blueprintIndex, 1);
            this.removedBlueprints.push(removedBlueprint);
        }
    };

    public get listEventBlueprints(): ExtendedEventBlueprintDTO[] {
        return this.eventBlueprints.map(blueprint => ({
            ...blueprint,
            disabled: this.selectedBlueprints.some(selected => selected.blueprintId === blueprint.blueprintId)
        }));
    }

    @mobx.action
    public setAssignedBlueprints = (eventBlueprint: IEventBlueprintDTO): void => {
        const selectedBlueprint = this.eventBlueprints.find(blueprint => blueprint.blueprintId === eventBlueprint.blueprintId);
        if (selectedBlueprint) {
            this.selectedBlueprints.push(eventBlueprint);
        }
    };

    public get isNewOrderBookingPopupOpened(): boolean {
        return !!this.newEventBlueprintSubWindow && this.newEventBlueprintSubWindow.getStatus() !== SubWindowStatus.Closed;
    }

    @mobx.action
    public openAddOrderBookingEventBlueprintPopup = (eventBlueprint: string): void => {
        if (this.isNewOrderBookingPopupOpened) return;
        const selectedBlueprint = this.eventBlueprints.find(blueprint => blueprint.blueprintId === eventBlueprint);
        if (selectedBlueprint) {
            this.newEventBlueprintSubWindow = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage(ADD_EVENT_BLUEPRINT_LABEL_TEXT),
                componentToHost: OrderBookingEventBlueprintForm,
                componentToHostProps: {
                    blueprint: selectedBlueprint,
                    accountId: this.entity.accountId,
                    formSubmitAction: this.updateEventBlueprints
                }
            });
        }
    };

    public get isCreateEventBlueprintPopupOpened(): boolean {
        return !!this.createEventBlueprintSubWindow && this.createEventBlueprintSubWindow.getStatus() !== SubWindowStatus.Closed;
    }

    private getEventTypesUsedInAccount(): TEventTypeLabel[] {
        return this.eventBlueprints.reduce(
            (eventTypes, blueprint) => eventTypes.concat(blueprint.eventTypes ?? []),
            [] as TEventTypeLabel[]
        );
    }

    @mobx.action
    public openCreateEventBlueprintForm = (): void => {
        if (this.isCreateEventBlueprintPopupOpened) return;
        this.createEventBlueprintSubWindow = this.subWindowService.showSubWindow({
            title: this.intl.formatMessage(CREATE_EVENT_BLUEPRINT_TITLE_TEXT),
            componentToHost: EventBlueprintForm,
            componentToHostProps: {
                accountId: this.entity.accountId,
                intl: this.intl,
                eventTypesUsedInAccount: this.getEventTypesUsedInAccount(),
                apiCallSuccessCallback: () => {
                    void this.refreshEventBlueprintsAfterCreate().catch(handleApiException);
                }
            }
        });
    };

    // A newly created blueprint must NOT be added to the Order Booking configuration
    // automatically (OEP-35708). We only refresh the list so the new blueprint becomes
    // available for the user to select manually; the selection is persisted on Apply.
    public refreshEventBlueprintsAfterCreate = async (): Promise<void> => {
        await this.fetchEventBlueprints();
    };

    public updateEventBlueprints = async (blueprint: IEventBlueprintDTO): Promise<IEventBlueprintDTO> => {
        try {
            this.isUpdating = true;
            const orderBookingBlueprint = this.getOrderBookingBlueprintDTO(blueprint);
            const updatedBlueprint = await this.blueprintsDAO.updateOrderBookingBlueprintData({ ...orderBookingBlueprint });
            this.setAssignedBlueprints(updatedBlueprint);
            return updatedBlueprint;
        } catch (error) {
            handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.isUpdating = false;
            });
        }
    };

    public getOrderBookingBlueprintDTO = (blueprint: IEventBlueprintDTO): IOrderBookingBlueprintDTO => {
        const dto: IOrderBookingBlueprintDTO = {
            blueprintId: blueprint.blueprintId,
            accountId: blueprint.accountId,
            type: blueprint.type,
            publicName: blueprint.publicName,
            publicDescription: blueprint.publicDescription,
            participantLinkType: blueprint.participantLinkType,
            orderBookingEnabled: blueprint.orderBookingEnabled,
            eventAttributeIds: blueprint.eventAttributeIds || [],
        };

        return dto;
    };

    public get notificationMessage(): MessageDescriptor {
        return ORDER_BOOKING_DATA_UPDATED_TEXT;
    }

    public async apiCall(): Promise<void> {
        const response = await this.accountAdminDAO.updateOrderBookingData({
            ...this.entity.getCommonAPIPayload(),
            orderBookingEnabled: this.entity.enabled,
            internalNotificationEmails: this.entity.enabled ? this.getOrderBookingEmailsToDTO() : []
        });
        const blueprintsToUpdate = this.selectedBlueprints.filter(
            blueprint => !blueprint.orderBookingSubset ||
                !Array.isArray(blueprint.orderBookingSubset) ||
                !blueprint.orderBookingSubset.includes(OrderBookingSubsets.DEFAULT)
        );
        for (const blueprint of blueprintsToUpdate) {
            const blueprintDTO = this.getOrderBookingBlueprintDTO(blueprint);
            await this.blueprintsDAO.updateOrderBookingBlueprintData({
                ...blueprintDTO,
                orderBookingSubset: [OrderBookingSubsets.DEFAULT]
            });
        }
        for (const removedBlueprint of this.removedBlueprints) {
            const removedBlueprintDTO = this.getOrderBookingBlueprintDTO(removedBlueprint);
            await this.blueprintsDAO.updateOrderBookingBlueprintData({
                ...removedBlueprintDTO,
                orderBookingSubset: []
            });
        }
        this.setOrderBookingEmails(response?.internalNotificationEmails?.join(','));
        this.entity.setAdditionalData(response.internalNotificationEmails);
    }
    public async fetchEventBlueprints(): Promise<void> {
        try {
            const params = { accountId: this.entity.accountId };
            const blueprintsResponse = await this.blueprintsDAO.getEventBlueprintList(params);
            mobx.runInAction(() => {
                this.eventBlueprints = blueprintsResponse;
            });
        } catch (error) {
            handleApiException(error);
        }
    }

    public getEmailsValidationMessage = (): string => {
        const amountEmails = this.internalNotificationEmails?.split(',').length || 0;
        if (amountEmails > ORDERBOOKING_EMAIL_MAX_EMAILS_AMOUNT) {
            return this.intl.formatMessage(ENTER_MAXIMUM_TEN_EMAILS);
        }

        const isValidByEmails = emailListValidator(this.internalNotificationEmails);
        if (!isValidByEmails.valid) {
            return this.intl.formatMessage({
                description: 'Text label',
                ...isValidByEmails.validationMessage,
            });
        }
    };

    protected shouldInterruptSubmit(): boolean {
        return super.shouldInterruptSubmit() || !this.isFormValid();
    }

    protected onInteruptHandler(): boolean | void {
        if (!this.isFormValid()) {
            this.isFormChecked = true;
        }
    }

    private isFormValid = (): boolean => {
        return !(this.entity.enabled && this.getEmailsValidationMessage());
    };

    private getOrderBookingEmailsToDTO = (): string[] => {
        return this.internalNotificationEmails?.split(',')
            .filter(Boolean)
            .reduce((result, current) => {
                if (!!current) result.push(current.trim());
                return result;
            }, []);
    };

    @mobx.action
    public setFieldTouched = (): void => {
        if (this.entity.enabled) this.isFormChecked = false;
    };
    @mobx.action
    public setOrderBookingEmails = (emails: string): void => {
        this.internalNotificationEmails = emails;
    };
}

export { OrderBookingFormVM };